
import { BASE, FTSE_INDEX, LSE_DASHBOARD_CARD, PATH } from "../../modules/constants"
import { CHECKBOX, getBitByName } from "../../modules/checkbox_constants"
import HOOK from "../../components/hooks"
import { formatIntDate_D_MMM_YYYY, isWithinNDays } from "../../modules/utilities"
import { formatDateSmart, formatIntDate_D_MM, isSameDay, isWithinLastNDays } from "../../modules/dateFunctions"
import { filterAnnualReportsByCheckbox, filterApptsByCheckbox, filterListingChangesByCheckbox, filterNewsByCheckbox } from '../../components/layout/FilterSearchResults'
import { getChecked } from "../companiesHouse/pageFunctions/checkboxFunctions"
import { Tooltip } from "@mui/material"

var checkboxes, lseCompaniesMeta, lseApptsMeta, lseEventsMeta, newsMeta // from props
var favouriteChecked, delistedChecked

const minAppts = 100 // Minimum number of appts or resigns
const minApptDaysToGet = 7 // Minimum number of appts/resigs days to get
const maxNumApptDaysToLookBack = 0 // max num. days to look back to satisfy min apps. quota

const minNewsReleases = 100 // Minimum number of news releases
const maxNumNewsDaysToLookBack = 0 // max num. days to look back to satisfy min news quota

const minListingChanges = 90 // Minimum number of listing changes to show
const minListingDaysToGet = 90 // Minimum number of listing days to get
const maxNumListingChangesDaysToLookBack = 0  // max num. days to look back to satisfy min listing changes quota

const maxAnnualReports = 500
const minAnnualReportsDaysToGet = 30
const maxAnnualReportsDaysToLookBack = 30

const dashboardCards = props => {
  // console.log('dashboardCards props:', props)
  ({ checkboxes, lseApptsMeta, lseCompaniesMeta, lseEventsMeta, newsMeta } = props)

  favouriteChecked = getChecked({ checkboxes, hook: HOOK.FAVOURITE })[0]
  delistedChecked = getChecked({ checkboxes, hook: HOOK.DELISTED })[0]

  // Don't don't need to process stuff if no checkbox are checked
  const someIndexAndTypeChecked = !!checkboxes[HOOK.LSE_INDEX] && !!checkboxes[HOOK.COMPANY_TYPE]
  const someNewCategoryChecked = !!checkboxes[HOOK.NEWS_CATEGORY]

  const recentNews = someNewCategoryChecked && newsMeta
    ? getRecentNews({ minNewsReleases })
    : []

  const recentAppts = (someIndexAndTypeChecked || favouriteChecked) && lseApptsMeta.items
    ? getRecentAppts()
    : []

  const recentResigs = someIndexAndTypeChecked && lseApptsMeta.items
    ? getRecentResigs()
    : []

  const listingChanges = someIndexAndTypeChecked || delistedChecked || favouriteChecked
    ? getListingChanges()
    : []

  const annualReports = someIndexAndTypeChecked || favouriteChecked
    ? getAnnualReports()
    : []

  return [
    {
      id: LSE_DASHBOARD_CARD.NEWS,
      title: `${recentNews.length} News Release${plural(recentNews)} today`,
      rows: recentNews,
    },
    {
      id: LSE_DASHBOARD_CARD.APPOINTMENTS,
      title: `${recentAppts.length} Director Appointment${plural(recentAppts)} in last 7 days`,
      rows: recentAppts,
    },
    {
      id: LSE_DASHBOARD_CARD.LISTING_CHANGES,
      title: `${listingChanges.length} Change${plural(listingChanges)} to FTSE Index Constituents in last 90 days`,
      rows: listingChanges,
    },
    {
      id: LSE_DASHBOARD_CARD.RESIGNATIONS,
      title: `${recentResigs.length} Director Resignation${plural(recentResigs)} in last 7 days`,
      rows: recentResigs,
    },
    {
      id: LSE_DASHBOARD_CARD.ANNUAL_REPORTS,
      title: `${annualReports.length} Annual Report${plural(annualReports)} in last 30 days`,
      rows: annualReports
    },
  ]
}

// Returns "s" is array length is not 1
const plural = array => array.length !== 1 ? "s" : ""

//#region Appointments & resignations
const getRecentAppts = () => {
  // Filter the items, including only those within lastt n days
  var items = getApptsAndResigs({ dateProp: "from" })
  // console.log('getRecentAppts:',getRecentAppts)
  return createTheRows({ items })
}

const getRecentResigs = () => {
  // Filter the items, including only those within last n days
  var items = getApptsAndResigs({ dateProp: "until" })
  return createTheRows({ items })
}

const getApptsAndResigs = ({ dateProp }) => {
  var items = lseApptsMeta.items.filter(item => isWithinLastNDays(item.apptInfo[dateProp], minApptDaysToGet))

  assignFavourites(items)
  items = filterApptsByCheckbox({ checkboxes, favouriteChecked, items })

  // Sort by date: most recent (today) first
  items.sort((a, b) => b.date - a.date)

  if (items.length < minAppts) {
    var loop = 0
    const date = new Date() // today
    date.setDate(date.getDate() - minApptDaysToGet + 1) // The day before the the days we already have

    while (items.length < minAppts && loop++ < maxNumApptDaysToLookBack) {
      date.setDate(date.getDate() - 1) // day before that etc.
      items = [...items, ...lseApptsMeta.items.filter(item => isSameDay(item.apptInfo[dateProp], date))]

      assignFavourites(items)
      items = filterApptsByCheckbox({ checkboxes, favouriteChecked, items })

      // Cut the number down to the min
      items = items.slice(0, minAppts)
    }
  }

  return items
}

const createTheRows = ({ items }) => {
  assignCompanyProps(items)
  items.sort((a, b) => b.date - a.date)
  return createApptCellColumns(items)
}

const createApptCellColumns = array => array === undefined
  ? []
  : array.map(s =>
    [
      // If the logo url contains "www" then use as is.
      // Else, its on the S3 server and we need to add in prefix
      { logo: s.logoUrl }, // getlogo(s) },
      { link: `/${PATH[BASE.COMPANY]}/${s.companyNumber}` },
      { value: s.companyName },
      { value: s.commonInfo.name },
      { value: s.apptInfo.position.title },
      { date: s.date }
    ]
  )

const assignFavourites = items => {
  var companyMeta
  items.forEach(item => {
    companyMeta = lseCompaniesMeta.items[item.companyInfo.companyMetaIndex]
    item.favourite = companyMeta?.isFavourite
  })
}

const assignCompanyProps = (appts) => {
  var companyMeta
  var element
  for (let index = 0; index < appts.length; index++) {
    element = appts[index]
    // Aquire the lseCompanyMeta from lseCompaniesMeta via the companyMetaIndex
    companyMeta = lseCompaniesMeta.items[element.companyInfo.companyMetaIndex]
    element.companyName = companyMeta?.names[0]
    element.companyNumber = companyMeta?.ids[0]
    element.logoUrl = companyMeta?.logo
    // Assign either the apptInfo.until or apptInfo.from date to 'date'
    element.date = new Date(element.apptInfo.until ?? element.apptInfo.from) // JARGON.FUTURE_DATE???
  }
}
//#endregion

//#region news
const getRecentNews = ({ minNewsReleases }) => {
  const date = new Date() // today
  var newsReleases = newsMeta.filter(item => isSameDay(item.publicationDate, date))

  newsReleases = filterByCheckboxAndSortNewsReleases(newsReleases)

  var loop = 0
  // Add news releases from previous days until we have reached the minimum number
  // Limit the no. of times we iterate, since checkboxes might filter out everything!
  while (newsReleases.length < minNewsReleases && loop++ < maxNumNewsDaysToLookBack) {
    date.setDate(date.getDate() - 1)
    // Combine the releases we have with those prome the previous day
    newsReleases = [...newsReleases, ...newsMeta.filter(item => isSameDay(item.publicationDate, date))]
    newsReleases = filterByCheckboxAndSortNewsReleases(newsReleases)
    newsReleases = newsReleases.slice(0, minNewsReleases)
  }

  return newsReleases.map(s =>
    [
      { link: `/${PATH[BASE.NEWS_RELEASE]}/${s._id}` },
      { logo: s.logo },
      { tooltip: `published: ${formatDateSmart(s.publicationDate)}` },
      { value: s.company },
      { value: s.headline, },
      { date: new Date(s.publicationDate) },
    ],
  )
}

const filterByCheckboxAndSortNewsReleases = newsReleases => {
  newsReleases = filterNewsByCheckbox({ items: newsReleases, checkboxes, favouriteChecked, nexus: false })

  // Sort by publicationDate
  newsReleases.sort((a, b) => {
    const dateA = new Date(a.publicationDate).getTime()
    const dateB = new Date(b.publicationDate).getTime()
    return dateB - dateA
  })

  return newsReleases
}
//#endregion

//#region Listing Changes
const getListingChanges = () => {
  var items = lseEventsMeta.items.filter(item => isWithinNDays(item.date, minListingDaysToGet) && item.eventType === 1)

  // Add the company info
  for (let index = 0; index < items.length; index++) {
    const item = items[index]
    items[index] = getCompanyInfo(item)
  }

  items = filterListingChangesByCheckbox({ items: items, checkboxes, delistedChecked, favouriteChecked })

  // Sort items by date
  items.forEach(item => item.date = new Date(item.date))
  items = items.sort((a, b) => b.date - a.date)

  if (items.length < minListingChanges) {
    var loop = 0
    const date = new Date() // today
    date.setDate(date.getDate() - minListingDaysToGet + 1) // The day before the the days we already have

    // Get additional items until the quota is met of max days reached
    while (items.length < minListingChanges && loop++ < maxNumListingChangesDaysToLookBack) {
      date.setDate(date.getDate() - 1) // day before that etc.

      // Get items for the given date
      var newItems = lseEventsMeta.items.filter(item => isSameDay(item.date, date))

      for (let index = 0; index < newItems.length; index++)
        newItems[index] = getCompanyInfo(newItems[index])

      // Filter the new items
      newItems = filterListingChangesByCheckbox({ items: newItems, checkboxes, delistedChecked, favouriteChecked })

      // Sort new items by date
      newItems.forEach(item => item.date = new Date(item.date))
      newItems.sort((a, b) => b.date - a.date)

      // Concat existing and new items
      items = [...items, ...newItems]

      // Cut the number down to the min
      items = items.slice(0, minListingChanges)
    }
  }

  setIsForeignDeslistedProps(items)

  return items.map(event =>
    [
      { link: event.foreignDelisted ? null : `/${PATH[BASE.COMPANY]}/${event.companyNumber}` },
      { tooltip: event.foreignDelisted ? `${event.companyName} is a delisted foreign company so no link available` : "" },
      { logo: event.logoUrl },
      { value: event.companyName },
      { value: priorState(event) },
      { value: postState(event) },
      { date: event.date },
    ],
  )
}

const getCompanyInfo = recentChanges => {
  const companyMeta = lseCompaniesMeta.items.find(item => item.ids[0] === recentChanges.companyNumber)
  const previousIndexBit = getBitByName(CHECKBOX[HOOK.LSE_INDEX], recentChanges.priorState)
  return !companyMeta
    ? {
      ...recentChanges,
      previousIndexBit,
      aic: recentChanges.aic === "1",
    }
    : {
      ...recentChanges,
      aic: companyMeta.aic,
      countryCode: companyMeta.countryCode,
      favourite: companyMeta.isFavourite,
      name: companyMeta.names[0],
      industryBit: companyMeta.industryBit,
      indexBit: companyMeta.indexBit,
      previousIndexBit,
    }
}

// Set 'ForeignDeslisted' prop true for items which have no indexBit and no-uk company number format
const setIsForeignDeslistedProps = items => {
  for (let index = 0; index < items.length; index++)
    items[index].foreignDelisted = !items[index].indexBit && !isValidFormat(items[index].companyNumber)
}

// Test if company number is UK company registration number format
const isValidFormat = companyNumber => /^(?:\d{8}|[A-Za-z]{2}\d{6})$/.test(companyNumber)

const priorState = event => {
  return event.priorState === null
    ? `new entry to LSE`
    : event.postState === null
      ? `delisted from ${event.priorState}`
      : `left ${event.priorState}`
}

const postState = event => {
  // console.log('postState state:', event.postState)

  // Test if postState is a FTSE_INDEX or info
  const prefix = Object.values(FTSE_INDEX).includes(event.postState) ? "joined " : ""

  return event.priorState === null
    ? `${event.postState}`
    : event.postState === null
      ? ``
      : `${prefix} ${event.postState}`
}
//#endregion Listing Changes


//#region annual reports
const getAnnualReports = () => {
  // lseCompaniesMeta.items annualReport.dates are in milliseconds from 1970/1/1 / 1e6 (to reduce data size) so we need to convert
  const millisInDay = 24 * 60 * 60 * 1000; // hours * minutes * seconds * ms
  var nDaysAgoMillis = Math.trunc((Date.now() - (minAnnualReportsDaysToGet * millisInDay)) / 1e6) // Convert to format from backend
  var items = lseCompaniesMeta.items.filter(item => item.annualReport.dates?.last?.pub > nDaysAgoMillis)

  items.sort((a, b) => b.annualReport.dates.last.pub - a.annualReport.dates.last.pub)

  nDaysAgoMillis = Math.trunc((Date.now() - (maxAnnualReportsDaysToLookBack * millisInDay)) / 1e6) // Convert to format from backend
  items = lseCompaniesMeta.items.filter(item => item.annualReport.dates?.last?.pub > nDaysAgoMillis)

  items = filterAnnualReportsByCheckbox({ checkboxes, favouriteChecked, items })
  items.sort((a, b) => b.annualReport.dates.last.pub - a.annualReport.dates.last.pub)

  items = items.slice(0, maxAnnualReports)

  return createARCellColumns(items)
}

const createARCellColumns = array => array === undefined
  ? []
  : array.map(s =>
    [
      { logo: s.logo },
      { link: s.annualReport.url },
      { value: s.names[0] },
      { value: `FY: ${formatIntDate_D_MMM_YYYY(s.annualReport.dates.last.doc)}`, align: 'right', style: { whiteSpace: 'nowrap' } },
      { value: formatIntDate_D_MM(s.annualReport.dates.last.pub), align: 'right', style: { whiteSpace: 'nowrap' } },
    ]
  )

//#endregion
export default dashboardCards

